import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  subscribeMissionToSidebar,
  subscribeMissionToSidebarPointer,
} from '../../utils/missionToSidebarBridge';
import { renderIcon } from '../SprintDetail/FlowchartView/utils/renderIcon';
import { MissionIcon } from '../SprintDetail/shared/ToolbarPrimitives/icons';
import './styles.css';

const defaultMissionIcon = <MissionIcon />;

/**
 * Floating mission icon that follows the cursor when dragging a mission over the sidebar.
 *
 * PERFORMANCE: React renders only on show/hide/content changes. Cursor following is
 * done by writing a transform straight to the DOM node on every pointer event —
 * re-rendering per mousemove tanked the frame rate over the sidebar.
 */
const MissionToSidebarDragGhost = () => {
  const [dragState, setDragState] = useState(() => ({
    showDragGhost: false,
    taskName: '',
    taskIcon: null,
    lastPointer: null,
  }));
  const ghostRef = useRef(null);

  useEffect(() => {
    const unsubscribeState = subscribeMissionToSidebar((bridgeState) => {
      setDragState((prev) => {
        if (
          prev.showDragGhost === bridgeState.showDragGhost
          && prev.taskName === bridgeState.taskName
          && prev.taskIcon === bridgeState.taskIcon
        ) {
          return prev;
        }
        return bridgeState;
      });
    });

    const unsubscribePointer = subscribeMissionToSidebarPointer((pointer) => {
      const el = ghostRef.current;
      if (el && pointer) {
        el.style.transform = `translate(${pointer.x}px, ${pointer.y}px) translate(-4px, -50%)`;
      }
    });

    return () => {
      unsubscribeState();
      unsubscribePointer();
    };
  }, []);

  if (!dragState.showDragGhost) return null;

  const pointer = dragState.lastPointer || { x: -1000, y: -1000 };

  return createPortal(
    <div
      ref={ghostRef}
      className="mission-sidebar-drag-ghost"
      style={{
        transform: `translate(${pointer.x}px, ${pointer.y}px) translate(-4px, -50%)`,
      }}
      aria-hidden
    >
      <span className="mission-sidebar-drag-ghost__icon">
        {renderIcon(dragState.taskIcon, {
          fallback: defaultMissionIcon,
          size: '14px',
          defaultColor: 'rgba(255, 255, 255, 0.9)',
        })}
      </span>
      {dragState.taskName ? (
        <span className="mission-sidebar-drag-ghost__label">
          {dragState.taskName}
        </span>
      ) : null}
    </div>,
    document.body,
  );
};

export default MissionToSidebarDragGhost;
